import {
  createContext,
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  CommandPaletteOverlayContextValue,
  CommandPaletteProviderProps,
} from "@/components/commandPalette/commandPalette.interfaces";

const CommandPaletteOverlayContext =
  createContext<CommandPaletteOverlayContextValue | null>(null);

type CommandPalettePageTitle = {
  pathname: string;
  title: string;
};

const CommandPalettePageTitleContext =
  createContext<CommandPalettePageTitle | null>(null);
const CommandPalettePageTitlePublisherContext = createContext<
  (pageTitle: CommandPalettePageTitle) => void
>(() => undefined);

/**
 * Provides modal open state for workspace command palette UX.
 *
 * @param {CommandPaletteProviderProps} props - Command palette provider props.
 * @returns {JSX.Element} Command palette provider.
 */
export function CommandPaletteProvider(
  props: CommandPaletteProviderProps,
): JSX.Element {
  const { children } = props;
  const [isOpen, setOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState<CommandPalettePageTitle | null>(
    null,
  );

  const open = useCallback((): void => {
    setOpen(true);
  }, []);

  const close = useCallback((): void => {
    setOpen(false);
  }, []);

  const overlayValue = useMemo(
    (): CommandPaletteOverlayContextValue => ({
      close,
      isOpen,
      open,
    }),
    [close, isOpen, open],
  );

  const publishPageTitle = useCallback(
    (nextPageTitle: CommandPalettePageTitle): void => {
      setPageTitle((previousPageTitle) =>
        previousPageTitle?.pathname === nextPageTitle.pathname &&
        previousPageTitle.title === nextPageTitle.title
          ? previousPageTitle
          : nextPageTitle,
      );
    },
    [],
  );

  return (
    <CommandPaletteOverlayContext.Provider value={overlayValue}>
      <CommandPalettePageTitlePublisherContext.Provider
        value={publishPageTitle}
      >
        <CommandPalettePageTitleContext.Provider value={pageTitle}>
          {children}
        </CommandPalettePageTitleContext.Provider>
      </CommandPalettePageTitlePublisherContext.Provider>
    </CommandPaletteOverlayContext.Provider>
  );
}

/**
 * Returns command palette overlay controls.
 *
 * @throws {Error} When used outside CommandPaletteProvider.
 * @returns {CommandPaletteOverlayContextValue} Command palette overlay context value.
 */
export function useCommandPaletteOverlay(): CommandPaletteOverlayContextValue {
  const context = useContext(CommandPaletteOverlayContext);

  if (!context) {
    throw new Error("useCommandPaletteOverlay requires CommandPaletteProvider");
  }

  return context;
}

/**
 * Returns the title published by the current non-modal page header, if one is
 * available. The pathname guards against reusing a title from the previous
 * route while a new detail page is loading.
 *
 * @returns The active page title and pathname, when available.
 */
export function useCommandPalettePageTitle(): CommandPalettePageTitle | null {
  return useContext(CommandPalettePageTitleContext);
}

/**
 * Publishes a page-level title for contextual command-palette history. It is
 * intentionally a no-op outside the app shell so shared headers stay usable.
 *
 * @param params - Page title publication settings.
 * @param params.enabled - Whether the current header belongs to a full page.
 * @param params.pathname - Current route pathname.
 * @param params.title - Page or record title rendered by the header.
 */
export function usePublishCommandPalettePageTitle(params: {
  enabled: boolean;
  pathname: string;
  title?: string;
}): void {
  const { enabled, pathname, title } = params;
  const publishPageTitle = useContext(CommandPalettePageTitlePublisherContext);

  useEffect(() => {
    if (!enabled || !title) {
      return;
    }

    publishPageTitle({ pathname, title });
  }, [enabled, pathname, publishPageTitle, title]);
}
